import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Milk, Wheat, Beef, Soup, Boxes, Store } from 'lucide-react';
import SiteLayout, { PALETTE } from './SiteLayout';
import HeroProductLoop from '../../components/HeroProductLoop';
import DocumentJourney from '../../components/DocumentJourney';
import { CashelBlueLogoStrip, CashelBlueCaseStudy } from './CashelBlueShowcase';
import FounderBlock from './FounderBlock';
import SEO from '../../components/SEO';

const IMG_LANDSCAPE = 'https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=2000&q=80';
const IMG_LINE = 'https://images.unsplash.com/photo-1565008576549-57569a49371d?auto=format&fit=crop&w=2000&q=80';

const FoundersClubStrip = () => {
  const [seats, setSeats] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketing/founders-club`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setSeats(d))
      .catch(() => {});
  }, []);
  if (seats && seats.closed) return null;
  return (
    <Link to="/founders-club" data-testid="founders-club-strip" style={{ textDecoration: 'none', display: 'block' }}>
      <div style={{
        background: PALETTE.navy,
        borderTop: `1px solid rgba(255,255,255,0.08)`,
        borderBottom: `2px solid ${PALETTE.magenta}`,
        padding: '13px 32px',
      }}>
        <div style={{
          maxWidth: 1320, margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 16, flexWrap: 'wrap', textAlign: 'center',
        }}>
          <span style={{
            fontSize: 10.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase',
            color: '#fff', background: PALETTE.magenta, padding: '4px 10px', borderRadius: 3,
          }}>
            Founder’s Club
          </span>
          <span style={{ fontSize: 14, color: '#E5E7EB', fontWeight: 500 }}>
            30% off the Standard licence, locked for 24 months.
          </span>
          <span data-testid="founders-club-strip-seats" style={{ fontSize: 14, color: PALETTE.gold, fontWeight: 700 }}>
            Limited seats · closed once the quota is met
          </span>
          <span style={{ fontSize: 13.5, color: '#fff', fontWeight: 700, letterSpacing: '0.04em' }}>
            See the offer →
          </span>
        </div>
      </div>
    </Link>
  );
};

export default function SiteHome() {
  return (
    <SiteLayout>
      <SEO
        title="Keystone by CloudVoro, Operations software for European food manufacturers"
        description="The operations layer between your factory floor and your accounts system. Sales orders, batch traceability, dispatch notes, retailer EDI and COAs for dairy, bakery, meat, prepared and wholesale food manufacturers. From order to audit-ready dispatch, live in 30 days."
        path="/"
        keywords="food manufacturing software, food ERP, batch traceability software, dispatch notes, COA software, pallet labels, retailer EDI, catch-weight software, food traceability, HACCP records, kill sheet software, recipe management, food wholesale software, meat processing software, bakery ERP, dairy operations software"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            '@id': 'https://keystone.cloudvoro.com/#organization',
            name: 'CloudVoro',
            alternateName: 'Keystone by CloudVoro',
            url: 'https://keystone.cloudvoro.com/',
            logo: 'https://keystone.cloudvoro.com/brochure-assets/logos/keystone-horizontal-light-2048.png',
            description:
              'CloudVoro (Ireland) builds Keystone, the operations platform for food manufacturers. Sister company to Intellix IT Solutions (UK).',
            email: 'hello@cloudvoro.com',
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'hello@cloudvoro.com',
              telephone: '+353-85-181-0310',
              contactType: 'Sales',
              areaServed: ['IE', 'GB', 'EU'],
              availableLanguage: 'en',
            },
            sameAs: ['https://www.intellixit.com/'],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            '@id': 'https://keystone.cloudvoro.com/#localbusiness',
            name: 'CloudVoro',
            url: 'https://keystone.cloudvoro.com/',
            image: 'https://keystone.cloudvoro.com/brochure-assets/logos/keystone-horizontal-light-2048.png',
            telephone: '+353-85-181-0310',
            email: 'hello@cloudvoro.com',
            address: { '@type': 'PostalAddress', addressCountry: 'IE', addressRegion: 'Ireland' },
            areaServed: ['Ireland', 'United Kingdom', 'European Union'],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            '@id': 'https://keystone.cloudvoro.com/#software',
            name: 'Keystone by CloudVoro',
            alternateName: ['Keystone', 'CloudVoro Keystone'],
            applicationCategory: 'BusinessApplication',
            applicationSubCategory: 'Food Traceability & Manufacturing Operations Software',
            operatingSystem: 'Web, iOS, Android',
            softwareVersion: '2026.1',
            datePublished: '2026-02-10',
            inLanguage: ['en', 'hu', 'pl', 'de'],
            url: 'https://keystone.cloudvoro.com/',
            image: 'https://keystone.cloudvoro.com/product/dashboard.png',
            screenshot: [
              'https://keystone.cloudvoro.com/product/dashboard.png',
              'https://keystone.cloudvoro.com/product/order-entry.png',
              'https://keystone.cloudvoro.com/product/dispatches.png',
              'https://keystone.cloudvoro.com/product/mock-recall.png',
              'https://keystone.cloudvoro.com/product/compliance.png',
              'https://keystone.cloudvoro.com/product/inwards-milk-intake.png',
            ],
            description: 'The operations layer between the factory floor and the accounts system, purpose-built for European food manufacturers running batch-based, catch-weight production. Sales orders, weekly planning, batch traceability, dispatch notes, retailer EDI, catch-weight COAs, GS1-128 pallet labels and one-click mock recall, for dairy, bakery, meat, prepared and wholesale producers.',
            featureList: [
              'Batch traceability (forward + backward, BRCGS §3.9 ready)',
              'Sales order entry with customer-SKU mapping',
              'Weekly production planner',
              'Catch-weight case rounding and invoicing',
              'Dispatch notes and GS1-128 pallet labels',
              'Certificates of Analysis (COA) auto-built from QC data',
              'Retailer EDI: Tesco, Aldi, Lidl, M&S, Ocado, SuperValu',
              'One-click mock recall with signed PDF response pack',
              'Tamper-evident cryptographically-chained audit log',
              'Two-way sync with Sage, AccountsIQ, Xero, QuickBooks',
              'Milk and ingredient intake with supplier-lot tracking',
              'Silo movement ledger',
              'QC hold-and-release workflow',
              'Multi-site consolidation (Plus tier)',
              'Dedicated tenant server + database isolation',
              'Daily encrypted backups with point-in-time restore',
            ],
            keywords: 'food traceability software, batch traceability, catch-weight invoicing, dispatch software, retailer EDI, mock recall software, BRCGS traceability, IFS Food software, dairy software, cheesemaker software, bakery ERP, meat traceability, food manufacturing operations',
            offers: {
              '@type': 'Offer',
              name: 'Keystone licence, quoted to your operation',
              url: 'https://keystone.cloudvoro.com/site/pricing',
              availability: 'https://schema.org/InStock',
              eligibleRegion: [
                { '@type': 'Country', name: 'Ireland' },
                { '@type': 'Country', name: 'United Kingdom' },
                { '@type': 'AdministrativeArea', name: 'European Union' },
              ],
            },
            audience: {
              '@type': 'BusinessAudience',
              audienceType: 'Small and mid-market food manufacturers',
              geographicArea: [
                { '@type': 'Country', name: 'Ireland' },
                { '@type': 'Country', name: 'United Kingdom' },
                { '@type': 'AdministrativeArea', name: 'European Union' },
              ],
            },
            publisher: {
              '@type': 'Organization',
              name: 'CloudVoro',
              url: 'https://keystone.cloudvoro.com/',
              logo: 'https://keystone.cloudvoro.com/keystone-logo.png',
              sameAs: [
                'https://www.capterra.com/p/10044016/Keystone-Voro-Production-Suite/',
                'https://www.linkedin.com/company/cloudvoro/',
              ],
            },
            sameAs: [
              'https://www.capterra.com/p/10044016/Keystone-Voro-Production-Suite/',
              'https://www.linkedin.com/company/cloudvoro/',
            ],
            isAccessibleForFree: false,
            hasPart: [
              { '@type': 'CreativeWork', name: 'Cashel Blue case study', url: 'https://keystone.cloudvoro.com/case-study/cashel-blue-story' },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: 'How long does Keystone take to implement?', acceptedAnswer: { '@type': 'Answer', text: 'Pilots are scoped to go live in 30 days. Standard implementation is a fixed-fee project quoted in writing before you commit, sized to customer count, retailer integrations and historical data import.' } },
              { '@type': 'Question', name: 'How much does Keystone cost?', acceptedAnswer: { '@type': 'Answer', text: 'Keystone is quoted to the operation rather than sold at one published price. The licence is sized to production volume, number of products and sites, and the modules and integrations you need, so a small family cheesemaker and an established manufacturer receive different quotes. Implementation is a separate fixed fee. Everything is put in writing after one call.' } },
              { '@type': 'Question', name: 'What food manufacturers is Keystone built for?', acceptedAnswer: { '@type': 'Answer', text: 'Batch-based food manufacturers supplying retailers or foodservice: dairies and cheesemakers, bakeries, meat processors, prepared-food manufacturers, and food wholesalers/distributors with traceability requirements. Family-owned and mid-market, typically €2M–€50M turnover. Large plants with full MES or MRP requirements are not our target, we will refer you elsewhere.' } },
              { '@type': 'Question', name: 'Does Keystone support FSAI, HACCP and BRCGS workflows?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, Keystone is designed to support your FSAI, HACCP and BRCGS traceability workflows with an immutable audit log, signed exports and one-click full-chain trace. Formal product certification is on the 2026 roadmap.' } },
              { '@type': 'Question', name: 'Where is the data hosted?', acceptedAnswer: { '@type': 'Answer', text: 'AWS Ireland (eu-west-1, Dublin) by default. Each tenant runs in its own dedicated server with its own database, no shared infrastructure. Encrypted at rest, TLS 1.3 in transit.' } },
            ],
          },
        ]}
      />
      {/* HERO */}
      <section data-testid="site-home-hero" style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: PALETTE.cream,
        overflow: 'hidden',
        paddingTop: 110,
        paddingBottom: 60,
      }}>
        {/* Scenic backdrop on the right */}
        <div style={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0,
          width: '52%',
          backgroundImage: `url(${IMG_LANDSCAPE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'saturate(0.85) contrast(1.05)',
        }} className="hero-bg-img" />
        {/* Cream-to-transparent fade so headline reads */}
        <div style={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0,
          width: '52%',
          background: `linear-gradient(90deg, ${PALETTE.cream} 0%, rgba(250,248,245,0.55) 22%, transparent 50%)`,
        }} className="hero-bg-img" />
        {/* Subtle navy vignette at bottom right for depth */}
        <div style={{
          position: 'absolute',
          right: 0, bottom: 0,
          width: '52%', height: '60%',
          background: `linear-gradient(180deg, transparent 0%, rgba(10,37,64,0.18) 100%)`,
        }} className="hero-bg-img" />

        <div style={{
          position: 'relative',
          maxWidth: 1320,
          margin: '0 auto',
          padding: '0 32px',
          width: '100%',
          zIndex: 2,
          display: 'grid',
          gridTemplateColumns: '1fr 1.05fr',
          gap: 56,
          alignItems: 'center',
        }} className="hero-grid">
          <div style={{ maxWidth: 620 }}>
            <h1 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(38px, 4.8vw, 64px)',
              fontWeight: 800,
              lineHeight: 1.0,
              letterSpacing: '-0.025em',
              color: PALETTE.navy,
              margin: 0,
            }} data-testid="hero-h1">
              You shouldn’t need an enterprise ERP<br/>
              <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>to prove your food is safe.</em>
            </h1>

            <p style={{
              fontSize: 19,
              lineHeight: 1.5,
              color: PALETTE.slate,
              marginTop: 28,
              maxWidth: 580,
              fontWeight: 400,
            }}>
              We watched great food producers run million-euro businesses on spreadsheets, then get quoted six figures to fix it. So we built Keystone: operations software for European food manufacturers. Sales orders, weekly planning, batch traceability, dispatch notes, retailer EDI and COAs in one system, live in 30 days.
            </p>

            <div style={{
              marginTop: 26,
              maxWidth: 580,
              borderLeft: `2px solid ${PALETTE.magenta}`,
              paddingLeft: 18,
            }} data-testid="hero-founder-line">
              <div style={{
                fontSize: 10,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                fontWeight: 700,
                color: PALETTE.magenta,
              }}>Direct line</div>
              <p style={{
                fontSize: 14.5,
                lineHeight: 1.55,
                color: PALETTE.navy,
                fontWeight: 600,
                margin: '8px 0 0',
              }}>
                A direct line to the team that builds Keystone. No sales funnel, no ticket queue.
              </p>
              <a
                href="tel:+353851810310"
                data-testid="hero-direct-line-phone"
                className="hero-direct-line"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                +353 85 181 0310
              </a>
              <style>{`
                .hero-direct-line {
                  display: inline-flex;
                  align-items: center;
                  gap: 9px;
                  margin-top: 12px;
                  padding: 9px 16px 9px 14px;
                  border: 1px solid ${PALETTE.border};
                  border-radius: 999px;
                  background: rgba(255,255,255,0.72);
                  color: ${PALETTE.navy};
                  font-size: 14.5px;
                  font-weight: 700;
                  letter-spacing: 0.01em;
                  text-decoration: none;
                  transition: border-color 0.22s ease, color 0.22s ease, transform 0.22s ease, box-shadow 0.22s ease;
                }
                .hero-direct-line:hover {
                  border-color: ${PALETTE.magenta};
                  color: ${PALETTE.magenta};
                  transform: translateY(-1px);
                  box-shadow: 0 10px 24px rgba(10,37,64,0.10);
                }
              `}</style>
            </div>

            <div style={{ display: 'flex', gap: 14, marginTop: 44, flexWrap: 'wrap' }}>
              <Link
                to="/site/contact"
                data-testid="hero-cta-primary"
                style={{
                  padding: '16px 28px',
                  background: PALETTE.navy,
                  color: '#fff',
                  textDecoration: 'none',
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  borderRadius: 4,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                Book a 20-minute fit review <span>→</span>
              </Link>
              <Link
                to="/site/pricing"
                data-testid="hero-cta-secondary"
                style={{
                  padding: '16px 28px',
                  background: 'transparent',
                  color: PALETTE.navy,
                  textDecoration: 'underline',
                  textUnderlineOffset: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                }}
              >
                How we price
              </Link>
            </div>

            <div style={{
              marginTop: 64,
              display: 'flex',
              gap: 36,
              flexWrap: 'wrap',
              fontSize: 12,
              color: PALETTE.muted,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontWeight: 600,
            }}>
              <span>BRCGS traceability requirements</span>
              <span style={{ color: PALETTE.border }}>·</span>
              <span>IFS Food</span>
              <span style={{ color: PALETTE.border }}>·</span>
              <span>EU food safety regulations</span>
            </div>
          </div>

          {/* Right column: silent auto-playing product demo loop */}
          <HeroProductLoop />
        </div>

        <style>{`
          @media (max-width: 1100px) {
            .hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
            .hero-product { justify-content: flex-start !important; }
            .hero-product > div:first-child { transform: none !important; max-width: 100% !important; }
          }
          @media (max-width: 900px) {
            .hero-bg-img { display: none !important; }
          }
          /* Mobile: drop full-viewport height so Google's smartphone screenshot
             (~412×823) and real phones show the H1 + lead + CTA above the fold
             instead of just the navbar. The dashboard mockup follows naturally. */
          @media (max-width: 768px) {
            [data-testid="site-home-hero"] {
              min-height: 0 !important;
              padding-top: 88px !important;
              padding-bottom: 40px !important;
              align-items: flex-start !important;
            }
            [data-testid="site-home-hero"] .hero-grid {
              grid-template-columns: minmax(0, 1fr) !important;
              gap: 28px !important;
              padding: 0 !important;
            }
            [data-testid="site-home-hero"] .hero-grid > div { min-width: 0; max-width: 100% !important; }
            [data-testid="site-home-hero"] [data-testid="hero-cta-primary"] {
              width: 100%;
              justify-content: center;
              padding: 15px 18px !important;
              font-size: 13px !important;
            }
            [data-testid="site-home-hero"] [data-testid="hero-cta-secondary"] {
              width: 100%;
              text-align: center;
              padding: 12px 18px !important;
            }
            [data-testid="site-home-hero"] .hero-grid > div:first-child > div:last-of-type { margin-top: 36px !important; gap: 10px 14px !important; }
            [data-testid="site-home-hero"] h1 {
              font-size: clamp(30px, 7.4vw, 40px) !important;
              line-height: 1.05 !important;
              margin: 0 0 16px !important;
            }
            [data-testid="site-home-hero"] p {
              font-size: 15.5px !important;
              line-height: 1.55 !important;
            }
          }
        `}</style>
      </section>

      {/* TRUST STRIP, Cashel Blue */}
      {/* Founder's Club promo strip removed from public marketing surface
          per commercial-positioning brief. Launch Partner terms are now
          discussed one-on-one after a written scope, not published on
          the homepage. */}

      <CashelBlueLogoStrip />

      {/* HOW IT WORKS, 5-step PO → invoice flow (compact home version) */}
      <DocumentJourney showCta />

      {/* OPERATIONAL DEPTH, the 8 small moves the big vendors round off.
          This is the new positioning: not "another traceability tool" but
          "the operations layer the big vendors round off". */}
      <section data-testid="operational-depth" style={{
        background: PALETTE.paper,
        padding: '110px 32px',
        borderTop: `1px solid ${PALETTE.border}`,
        borderBottom: `1px solid ${PALETTE.border}`,
      }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{ maxWidth: 760, marginBottom: 56 }}>
            <div style={{ fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 20 }}>
              Where generic ERPs quietly fail
            </div>
            <h2 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(32px, 4.2vw, 48px)',
              fontWeight: 700, color: PALETTE.navy,
              lineHeight: 1.05, letterSpacing: '-0.02em', margin: '0 0 22px',
            }}>
              Eight small moves 
              <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>
                the big vendors round off.
              </em>
            </h2>
            <p style={{ fontSize: 17, lineHeight: 1.65, color: PALETTE.slate, margin: 0 }}>
              Odoo, Business Central, Sage, every one of them can invoice a wholesaler. None of them, out of the box, hold the operational shape of a real food business. Here’s what Keystone quietly does that the others need a five-figure customisation project to attempt.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18 }} className="ops-depth-grid">
            {[
              { t: 'Customer-SKU mapping', b: 'Every retailer’s code stored against your internal SKU. Dispatch notes print in their language automatically.' },
              { t: 'Catch-weight case rounding', b: 'Nominal 1.4 kg × 12 rounds to <strong>actual weighed</strong> totals on the invoice, per case, per pallet.' },
              { t: 'Blind-spot recall sweep', b: 'Traceability that starts from an ingredient lot, not just a finished batch. Nothing hides upstream.' },
              { t: 'Tamper-evident audit chain', b: 'Every event cryptographically chained. You can’t rewrite yesterday without leaving a signed break.' },
              { t: 'Mock recall in one click', b: 'Any starting point → downstream customers + draft comms + PDF response pack. In seconds.' },
              { t: 'Batch corrections, no history loss', b: 'Re-label a batch six months later. Old record preserved, correction signed, trace still runs.' },
              { t: 'Co-branding as first-class', b: 'Same batch, three retailer wrappers, private-label variants, all traceable in one chain.' },
              { t: 'Ingredient-lot forward trace', b: 'Supplier recalls flour lot #X? Affected finished batches on screen before the coffee’s cold.' },
            ].map((m, i) => (
              <div key={m.t} data-testid={`ops-move-${i + 1}`} style={{
                padding: '20px 20px 22px',
                background: PALETTE.cream,
                borderLeft: `2px solid ${PALETTE.magenta}`,
                borderRadius: 4,
              }}>
                <div style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 20, fontWeight: 700, color: PALETTE.magenta,
                  lineHeight: 1, marginBottom: 10,
                }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: PALETTE.navy, lineHeight: 1.3, marginBottom: 8 }}>
                  {m.t}
                </div>
                <div style={{ fontSize: 13, color: PALETTE.slate, lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: m.b }} />
              </div>
            ))}
          </div>

          <div style={{
            marginTop: 44,
            padding: '22px 28px',
            background: PALETTE.navy,
            color: '#fff',
            borderLeft: `3px solid ${PALETTE.gold}`,
            borderRadius: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 24,
            flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: PALETTE.gold, fontWeight: 700, marginBottom: 4 }}>
                The pattern
              </div>
              <div style={{ fontSize: 15, lineHeight: 1.5, color: '#fff', maxWidth: 660 }}>
                Every one of these exists because a real food manufacturer, not a product manager in an office, asked for it. That’s what “purpose-built” looks like in practice.
              </div>
            </div>
            <Link
              to="/site/contact"
              data-testid="ops-depth-contact-cta"
              style={{
                padding: '12px 20px',
                background: PALETTE.gold,
                color: PALETTE.navy,
                textDecoration: 'none',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                borderRadius: 4,
                whiteSpace: 'nowrap',
              }}
            >
              Talk to us →
            </Link>
          </div>
        </div>

        <style>{`
          @media (max-width: 1000px) {
            .ops-depth-grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
          @media (max-width: 600px) {
            .ops-depth-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </section>

      {/* ADAPTIVE INTELLIGENCE, "software that learns the operation" */}
      <section data-testid="adaptive-intelligence" style={{
        background: PALETTE.navy,
        color: '#fff',
        padding: '110px 32px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: '#C9A961', marginBottom: 22 }}>
            Adaptive by design
          </div>
          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(32px, 4.4vw, 52px)',
            fontWeight: 700, lineHeight: 1.06, letterSpacing: '-0.02em',
            margin: '0 0 26px', maxWidth: 1040,
          }}>
            Your factory should not have to adapt to software.<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: '#C9A961' }}>Keystone adapts to your factory.</em>
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 56, alignItems: 'center' }} className="adaptive-grid">
            <div style={{ maxWidth: 560 }}>
              <p style={{ fontSize: 17, lineHeight: 1.7, color: 'rgba(255,255,255,0.85)', margin: '0 0 18px' }}>
                Most systems are configured once and expect your people to work around them. Keystone learns from real operational behaviour.
              </p>
              <p style={{ fontSize: 17, lineHeight: 1.7, color: 'rgba(255,255,255,0.85)', margin: '0 0 18px' }}>
                Customer-specific quantities, weights, units and recurring workflow patterns are learned from actual use and applied automatically. When today’s order is different, the operator simply overrides the suggestion.
              </p>
              <p style={{ fontSize: 17, lineHeight: 1.7, color: '#fff', margin: '0 0 26px', fontWeight: 600 }}>
                Less configuration. Less repetitive entry. More intelligence with every real interaction.
              </p>
              <div style={{ borderLeft: `3px solid ${PALETTE.magenta}`, paddingLeft: 18, margin: '0 0 8px' }}>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 21, fontWeight: 700, lineHeight: 1.35, letterSpacing: '-0.01em', color: '#fff' }}>
                  Every time your team teaches Keystone something,<br/><em style={{ fontStyle: 'italic', fontWeight: 500, color: '#C9A961' }}>they shouldn’t have to teach it twice.</em>
                </div>
              </div>
            </div>

            {/* Learn → apply → override mini diagram */}
            <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '30px 28px' }}>
              {[
                { k: 'First order', v: 'Cases entered. Weight and units confirmed by the operator.', tag: 'Keystone watches' },
                { k: 'Keystone learns', v: 'The customer-specific relationship is remembered from real use.', tag: 'No formula to maintain', accent: true },
                { k: 'Next order', v: 'Weight and units are suggested automatically, before anyone types.', tag: 'Work removed' },
                { k: 'Operator decides', v: 'Accept the suggestion, or override it whenever reality is different.', tag: 'People stay in control', gold: true },
              ].map((s, i) => (
                <div key={i} style={{ position: 'relative', paddingLeft: 22, paddingBottom: i === 3 ? 0 : 22 }}>
                  <div style={{ position: 'absolute', left: 0, top: 4, width: 10, height: 10, borderRadius: '50%', background: s.gold ? '#C9A961' : s.accent ? PALETTE.magenta : 'rgba(255,255,255,0.5)' }} />
                  {i !== 3 && <div style={{ position: 'absolute', left: 4.5, top: 14, bottom: 0, width: 1, background: 'rgba(255,255,255,0.18)' }} />}
                  <div style={{ fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: s.gold ? '#C9A961' : PALETTE.magenta, marginBottom: 4 }}>{s.tag}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{s.k}</div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'rgba(255,255,255,0.72)' }}>{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 48, paddingTop: 28, borderTop: '1px solid rgba(255,255,255,0.14)', textAlign: 'center' }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(22px, 2.6vw, 30px)', fontWeight: 700, letterSpacing: '-0.015em', color: '#fff' }}>
              Automation where it helps. <em style={{ fontStyle: 'italic', fontWeight: 500, color: '#C9A961' }}>Human judgement where it matters.</em>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 860px) {
            .adaptive-grid { grid-template-columns: 1fr !important; gap: 36px !important; }
          }
        `}</style>
      </section>

      {/* POSITIONING BAND, "Production layer between accounts and factory floor" */}
      <section data-testid="positioning-band" style={{
        background: PALETTE.cream,
        padding: '96px 32px',
        borderTop: `1px solid ${PALETTE.border}`,
        borderBottom: `1px solid ${PALETTE.border}`,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', position: 'relative' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '0.85fr 1.15fr',
            gap: 72,
            alignItems: 'center',
          }} className="positioning-grid">
            <div>
              <div style={{
                fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
                fontWeight: 700, color: PALETTE.magenta, marginBottom: 20,
              }}>
                Not another ERP
              </div>
              <h2 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(30px, 3.6vw, 46px)',
                fontWeight: 700, color: PALETTE.navy,
                lineHeight: 1.08, letterSpacing: '-0.02em', margin: '0 0 22px',
              }}>
                Keep your accounts package.{' '}
                <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>
                  Fill the gap it was never designed to cover.
                </em>
              </h2>
              <p style={{
                fontSize: 16.5, lineHeight: 1.6, color: PALETTE.slate,
                margin: 0, maxWidth: 500,
              }}>
                Finance owns invoices and VAT. <strong style={{ color: PALETTE.navy, fontWeight: 700 }}>Keystone owns everything else</strong>, orders, planning, batches, dispatch, COAs, pallet labels, EDI.
              </p>
            </div>

            {/* Visual stack, the three-tier diagram */}
            <div data-testid="positioning-stack" style={{
              display: 'flex', flexDirection: 'column', gap: 0,
              border: `1px solid ${PALETTE.border}`, background: PALETTE.paper,
              boxShadow: '0 30px 60px rgba(10,37,64,0.06)',
            }}>
              {[
                {
                  eyebrow: 'Layer 03, Finance',
                  title: 'Sage · AccountsIQ · Xero · QuickBooks',
                  body: 'Customers, suppliers, invoices, VAT, payments, ledger.',
                  accent: PALETTE.gold,
                  bg: PALETTE.paper,
                },
                {
                  eyebrow: 'Layer 02, Production · Keystone',
                  title: 'The operations layer for your factory.',
                  body: 'Sales orders · weekly planning · batch traceability · dispatch · COAs · pallet labels · retailer EDI.',
                  accent: PALETTE.magenta,
                  bg: PALETTE.bgSoft,
                  highlight: true,
                },
                {
                  eyebrow: 'Layer 01, Factory floor',
                  title: 'Your QA, dispatch and production team',
                  body: 'The people who actually pack, weigh, ship and sign off.',
                  accent: PALETTE.navy,
                  bg: PALETTE.paper,
                },
              ].map((row) => (
                <div key={row.eyebrow} style={{
                  padding: row.highlight ? '32px 30px' : '24px 30px',
                  borderTop: `1px solid ${PALETTE.border}`,
                  borderLeft: `4px solid ${row.accent}`,
                  background: row.bg,
                  position: 'relative',
                }}>
                  {row.highlight && (
                    <div style={{
                      position: 'absolute', top: 14, right: 18,
                      fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
                      fontWeight: 700, color: PALETTE.magenta,
                      background: PALETTE.paper, padding: '4px 10px',
                      border: `1px solid ${PALETTE.magenta}33`,
                    }}>
                      Keystone fits here
                    </div>
                  )}
                  <div style={{
                    fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
                    fontWeight: 700, color: row.accent, marginBottom: 6,
                  }}>
                    {row.eyebrow}
                  </div>
                  <div style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: row.highlight ? 21 : 17, fontWeight: 700, color: PALETTE.navy,
                    margin: '0 0 6px', letterSpacing: '-0.01em', lineHeight: 1.2,
                  }}>
                    {row.title}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.55, color: PALETTE.slate }}>
                    {row.body}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 960px) {
            [data-testid="positioning-band"] .positioning-grid {
              grid-template-columns: 1fr !important;
              gap: 40px !important;
            }
          }
        `}</style>
      </section>

      {/* BEST FIT FOR, self-qualification band with iconography & editorial layout */}
      <section data-testid="best-fit-for" style={{
        background: PALETTE.ink,
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
        padding: '110px 32px',
        borderTop: `1px solid ${PALETTE.border}`,
      }}>
        {/* Subtle gold radial in top-right corner for depth */}
        <div aria-hidden="true" style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: 720,
          height: 720,
          background: 'radial-gradient(circle, rgba(201,169,97,0.16) 0%, rgba(201,169,97,0) 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: 1240, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Header strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 56, alignItems: 'end', marginBottom: 56 }} className="bf-header">
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 18 }}>
                Self-qualify
              </div>
              <h2 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(34px, 4.4vw, 56px)',
                fontWeight: 700,
                color: '#fff',
                margin: 0,
                letterSpacing: '-0.02em',
                lineHeight: 1.04,
              }}>
                Best fit for.<br/>
                <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.gold }}>Six clear yeses.</em>
              </h2>
            </div>
            <div style={{
              borderLeft: `2px solid ${PALETTE.gold}`,
              paddingLeft: 24,
            }}>
              <p style={{ fontSize: 16, lineHeight: 1.65, color: '#CBD5E1', margin: 0, maxWidth: 480 }}>
                Keystone is built for <strong style={{ color: '#fff' }}>family-owned and mid-market</strong> batch-based food manufacturers selling to retailers or foodservice, dairy, bakery, meat, prepared, wholesale. If you're on this list, we'll fit. Large plants with full MES / MRP requirements, we'll be honest and refer you elsewhere.
              </p>
            </div>
          </div>

          {/* 6-card grid with icons */}
          <ul style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 24,
          }}>
            {[
              { n: '01', Icon: Milk,  t: 'Dairies & cheesemakers',                d: 'Variable-weight batches, ageing windows, retailer SKU mapping, split-batch dispatch, catch-weight invoicing.' },
              { n: '02', Icon: Wheat, t: 'Bakeries',                              d: 'Short shelf-life, allergen control, dispatch waves, retailer / foodservice mix, best-before per batch.' },
              { n: '03', Icon: Beef,  t: 'Meat processors',                       d: 'Kill-sheet reconciliation, forward + backward traceability, supplier locking, COA per batch, first-pass yield.' },
              { n: '04', Icon: Soup,  t: 'Prepared food manufacturers',           d: 'Multi-component recipes, allergen ladders, customer SKU mapping, retailer pallet labels, mass-balance audits.' },
              { n: '05', Icon: Boxes, t: 'Food wholesalers & distributors',       d: 'Batch-tracked stock, customer-specific codes, dispatch + EDI, chill-chain temperature commitments.' },
              { n: '06', Icon: Store, t: 'Retailer & foodservice suppliers',      d: 'Tesco, Lidl, M&S, SuperValu, Centra, SPAR, Musgrave, formatted dispatch notes, ASNs, chargeback-safe pallet labels.' },
            ].map(({ n, Icon, t, d }) => (
              <li
                key={n}
                className="bf-card"
                style={{
                  position: 'relative',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 6,
                  padding: '28px 26px 26px',
                  transition: 'transform 220ms ease, border-color 220ms ease, background 220ms ease',
                  overflow: 'hidden',
                }}
              >
                {/* Number eyebrow */}
                <div style={{
                  position: 'absolute',
                  top: 18,
                  right: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  color: 'rgba(201,169,97,0.45)',
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontStyle: 'italic',
                }}>{n}</div>

                {/* Icon badge */}
                <div className="bf-icon" style={{
                  width: 52,
                  height: 52,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, rgba(201,169,97,0.18) 0%, rgba(201,169,97,0.06) 100%)',
                  border: '1px solid rgba(201,169,97,0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 22,
                  transition: 'background 220ms ease, transform 220ms ease',
                }}>
                  <Icon size={24} strokeWidth={1.6} color={PALETTE.gold} />
                </div>

                <div style={{
                  fontSize: 17.5,
                  fontWeight: 700,
                  color: '#fff',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.25,
                  marginBottom: 10,
                  fontFamily: "'Playfair Display', Georgia, serif",
                }}>
                  {t}
                </div>
                <div style={{
                  fontSize: 13.5,
                  color: '#94A3B8',
                  lineHeight: 1.6,
                }}>
                  {d}
                </div>

                {/* Hover accent line */}
                <div className="bf-line" aria-hidden="true" style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  height: 2,
                  width: 0,
                  background: PALETTE.magenta,
                  transition: 'width 320ms ease',
                }} />
              </li>
            ))}
          </ul>

          {/* Honest closer */}
          <div style={{
            marginTop: 48,
            padding: '20px 26px',
            borderLeft: `3px solid ${PALETTE.magenta}`,
            background: 'rgba(214, 51, 108, 0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            flexWrap: 'wrap',
          }}>
            <div style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: PALETTE.magenta,
              flexShrink: 0,
            }}>
              Not on this list?
            </div>
            <div style={{ fontSize: 14, color: '#CBD5E1', lineHeight: 1.55, flex: 1, minWidth: 280 }}>
              Talk to us anyway. If we don't fit, we'll point you at someone who does, for free, no quota, no commission.
            </div>
            <Link
              to="/site/contact"
              style={{
                color: PALETTE.gold,
                fontSize: 13,
                fontWeight: 700,
                textDecoration: 'none',
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
              }}
              data-testid="best-fit-fallback-cta"
            >
              Talk to us →
            </Link>
          </div>
        </div>

        <style>{`
          [data-testid="best-fit-for"] .bf-card:hover {
            background: rgba(255,255,255,0.05);
            border-color: rgba(201,169,97,0.40);
            transform: translateY(-3px);
          }
          [data-testid="best-fit-for"] .bf-card:hover .bf-icon {
            background: linear-gradient(135deg, rgba(201,169,97,0.32) 0%, rgba(201,169,97,0.10) 100%);
            transform: scale(1.05);
          }
          [data-testid="best-fit-for"] .bf-card:hover .bf-line {
            width: 100%;
          }
          @media (max-width: 880px) {
            [data-testid="best-fit-for"] .bf-header { grid-template-columns: 1fr !important; gap: 28px !important; align-items: start !important; }
          }
        `}</style>
      </section>

      {/* WHAT YOU'RE RUNNING TODAY */}
      <section style={{ background: PALETTE.cream, padding: '96px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 80, alignItems: 'end', marginBottom: 48 }} className="stack-head">
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>What you’re running today</div>
              <h2 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(34px, 4.6vw, 54px)', fontWeight: 700, color: PALETTE.navy, margin: 0, lineHeight: 1.05, letterSpacing: '-0.02em',
              }}>
                Four systems.<br/>
                <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>None of them own the batch.</em>
              </h2>
            </div>
            <p style={{ fontSize: 17, lineHeight: 1.55, color: PALETTE.slate, margin: 0, maxWidth: 520 }}>
              The batch, the retailer chargeback and the audit trail live in the gaps between them. <strong style={{ color: PALETTE.navy, fontWeight: 700 }}>Keystone owns the gaps.</strong>
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, border: `1px solid ${PALETTE.border}`, background: PALETTE.paper }} className="stack-grid">
            {[
              { t: 'Accounts package', tag: 'Ledger', d: 'Sage, Xero, AccountsIQ. Great at invoices, blind to batches.' },
              { t: 'Vertical ERP', tag: 'Half-fits', d: 'Built for one sub-sector. Painful if you sit between two.' },
              { t: 'Generic ERP', tag: 'Enterprise', d: 'Twelve-month project. Ops still runs on Excel afterwards.' },
              { t: 'Bolt-on tools', tag: 'Point tools', d: 'Traceability, dispatch, EDI, three vendors, three invoices, no owner.' },
            ].map((s, i) => (
              <div key={s.t} style={{
                padding: 28,
                borderRight: (i + 1) % 4 === 0 ? 'none' : `1px solid ${PALETTE.border}`,
                position: 'relative',
              }} className="stack-cell">
                <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.muted, marginBottom: 12 }}>
                  {s.tag}
                </div>
                <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 21, fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.01em', lineHeight: 1.15 }}>
                  {s.t}
                </h3>
                <p style={{ fontSize: 13.5, lineHeight: 1.55, color: PALETTE.slate, marginTop: 10, marginBottom: 0 }} dangerouslySetInnerHTML={{ __html: s.d }} />
              </div>
            ))}
          </div>

          <div style={{
            marginTop: 24,
            padding: '18px 26px',
            background: PALETTE.navy,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 20,
            flexWrap: 'wrap',
          }} className="stack-cta">
            <div style={{ fontSize: 14.5, color: '#E5E7EB' }}>
              <strong style={{ color: PALETTE.gold, fontWeight: 700 }}>Keep your accounts package.</strong> We replace the spreadsheets on top.
            </div>
            <Link to="/site/contact" style={{
              padding: '11px 20px',
              background: PALETTE.gold,
              color: PALETTE.navy,
              textDecoration: 'none',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              borderRadius: 4,
              flexShrink: 0,
            }}>
              See how it slots in →
            </Link>
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) {
            .stack-head { grid-template-columns: 1fr !important; gap: 24px !important; align-items: start !important; }
            .stack-grid { grid-template-columns: 1fr 1fr !important; }
            .stack-grid > .stack-cell { border-right: none !important; border-bottom: 1px solid ${PALETTE.border}; }
            .stack-grid > .stack-cell:nth-child(odd) { border-right: 1px solid ${PALETTE.border} !important; }
          }
          @media (max-width: 540px) {
            .stack-grid { grid-template-columns: 1fr !important; }
            .stack-grid > .stack-cell { border-right: none !important; }
          }
        `}</style>
      </section>

      {/* PROOF / NUMBERS */}
      <section style={{ background: PALETTE.navy, color: '#fff', padding: '90px 32px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 40 }} className="stats-grid">
          {[
            { num: '< 4s', label: 'Dispatch note generation', sub: 'From order to PDF' },
            { num: '100%', label: 'Forward & backward trace', sub: 'BRC BRCGS traceability requirements § 5.4 ready' },
            { num: '15+', label: 'Years building bespoke', sub: 'European manufacturing experience' },
            { num: '0', label: 'Spreadsheets needed', sub: 'After day one' },
          ].map(s => (
            <div key={s.label}>
              <div style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(40px, 5vw, 64px)',
                fontWeight: 700,
                lineHeight: 1,
                color: PALETTE.gold,
                letterSpacing: '-0.02em',
              }}>{s.num}</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 14, letterSpacing: '0.02em' }}>{s.label}</div>
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>{s.sub}</div>
            </div>
          ))}
        </div>
        <style>{`
          @media (max-width: 800px) { .stats-grid { grid-template-columns: 1fr 1fr !important; gap: 32px !important; } }
          @media (max-width: 480px) { .stats-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </section>

      {/* PROBLEM / SOLUTION SPLIT */}
      <section style={{ background: PALETTE.cream, padding: '120px 32px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 16, textAlign: 'center' }}>
            The boring truth
          </div>
          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(32px, 4.4vw, 52px)',
            fontWeight: 700,
            textAlign: 'center',
            color: PALETTE.navy,
            margin: 0,
            letterSpacing: '-0.02em',
            lineHeight: 1.05,
          }}>
            Two ways you lose money tonight.
          </h2>

          <div style={{
            marginTop: 72,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 0,
            border: `1px solid ${PALETTE.border}`,
          }} className="ps-grid">
            <div style={{ padding: 48, borderRight: `1px solid ${PALETTE.border}`, background: PALETTE.paper }}>
              <div style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.muted, marginBottom: 18 }}>Without Keystone</div>
              <h3 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 30, fontWeight: 700, color: PALETTE.ink, margin: 0, lineHeight: 1.2,
              }}>The 11pm phone call.</h3>
              <p style={{ fontSize: 16, lineHeight: 1.65, color: PALETTE.slate, marginTop: 18 }}>
                A retailer flags a batch. You're paging through six spreadsheets and a printed picking list. The QC manager is asleep. The auditor wants source-to-shelf in 60 minutes. Three people are typing into different versions of the same Excel file.
              </p>
              <ul style={{ marginTop: 24, padding: 0, listStyle: 'none' }}>
                {['Hand-written dispatch notes', 'Lost batch lineage', 'Manual COA assembly', 'Reactive QC, not preventive'].map(p => (
                  <li key={p} style={{ fontSize: 14, color: PALETTE.muted, padding: '6px 0', display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ width: 4, height: 4, background: PALETTE.muted, borderRadius: '50%' }} />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ padding: 48, background: PALETTE.navy, color: '#fff' }}>
              <div style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 18 }}>With Keystone</div>
              <h3 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 30, fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.2,
              }}>The 11pm shrug.</h3>
              <p style={{ fontSize: 16, lineHeight: 1.65, color: '#CBD5E1', marginTop: 18 }}>
                One search. Batch number. Forward trace, backward trace, COAs, dispatch records, temperature logs, exported as a single PDF in under four seconds. The auditor goes home. You go to bed.
              </p>
              <ul style={{ marginTop: 24, padding: 0, listStyle: 'none' }}>
                {['1-click dispatch & COA', 'Live batch genealogy', 'Auto-generated audit packs', 'QC alerts before deviation'].map(p => (
                  <li key={p} style={{ fontSize: 14, color: '#E5E7EB', padding: '6px 0', display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ width: 4, height: 4, background: PALETTE.gold, borderRadius: '50%' }} />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 800px) {
            .ps-grid { grid-template-columns: 1fr !important; }
            .ps-grid > div:first-child { border-right: none !important; border-bottom: 1px solid ${PALETTE.border} !important; }
          }
        `}</style>
      </section>

      {/* CASHEL BLUE CASE STUDY */}
      <CashelBlueCaseStudy />

      {/* FOUNDER CREDIBILITY */}
      <FounderBlock />

      {/* HOSTING / ISOLATION / COMPLIANCE */}
      <section style={{ background: PALETTE.navy, color: '#fff', padding: '120px 32px', position: 'relative', overflow: 'hidden' }}>
        {/* Subtle decorative dots */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `radial-gradient(circle at 15% 20%, ${PALETTE.gold}11 0%, transparent 40%), radial-gradient(circle at 85% 80%, ${PALETTE.magenta}15 0%, transparent 45%)`,
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 80, alignItems: 'start', marginBottom: 72 }} className="iso-head">
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 16 }}>
                Hosting · Isolation · Compliance
              </div>
              <h2 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(32px, 4.4vw, 52px)', fontWeight: 700, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05,
              }}>
                Your factory.<br/>
                Your tenant.<br/>
                <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.gold }}>Your server.</em>
              </h2>
            </div>
            <p style={{ fontSize: 18, lineHeight: 1.65, color: '#CBD5E1', margin: 0, marginTop: 8 }}>
              Run Keystone on our managed EU AWS region (Dublin, eu-west-1), or deploy it inside your own AWS account, in your own VPC, behind your own firewall. <strong style={{ color: '#fff', fontWeight: 600 }}>Every tenant gets its own dedicated server instance.</strong> No shared databases. No noisy neighbours. No "the cluster is having a moment."
              <br/><br/>
              When Tesco's CSV import is hammering one customer's site, your Friday dispatch runs at full speed. When your auditor asks where your data lives, you point at one server, in one region, with one tenant on it, yours.
            </p>
          </div>

          {/* 4-tile principle grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 0,
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 4,
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.02)',
          }} className="iso-grid">
            {[
              {
                eyebrow: 'Tenant isolation',
                title: 'One client. One instance.',
                d: 'Each tenant runs in its own dedicated server with its own database and its own backup chain. Zero data co-mingling. Zero blast radius.',
              },
              {
                eyebrow: 'Deployment choice',
                title: 'Our cloud or yours.',
                d: 'Run Keystone on our managed AWS EU (Dublin, eu-west-1), or deploy into your own AWS account with our IaC templates. Your call.',
              },
              {
                eyebrow: 'Performance',
                title: 'No noisy neighbours.',
                d: 'Your CPU, your memory, your I/O. A spike at one customer never slows down another. Capacity scales per tenant, not per cluster.',
              },
              {
                eyebrow: 'Auditability',
                title: 'One server to point at.',
                d: 'When the auditor asks "where does the data live?", you answer in one sentence. Single region, single tenant, single chain of custody.',
              },
            ].map((t, i) => (
              <div key={t.title} style={{
                padding: '28px 28px 32px',
                borderRight: (i + 1) % 4 === 0 ? 'none' : '1px solid rgba(255,255,255,0.10)',
              }} className="iso-cell">
                <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 14 }}>
                  {t.eyebrow}
                </div>
                <h3 style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 22, fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.15, letterSpacing: '-0.01em',
                }}>{t.title}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.6, color: '#CBD5E1', marginTop: 12, marginBottom: 0 }}>
                  {t.d}
                </p>
              </div>
            ))}
          </div>

          {/* Compliance ribbon */}
          <div style={{
            marginTop: 72,
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: 56,
            alignItems: 'center',
          }} className="comp-row">
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>
                Built for compliance, not bolted on
              </div>
              <h3 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(26px, 3.4vw, 38px)', fontWeight: 700, color: '#fff', margin: 0, letterSpacing: '-0.01em', lineHeight: 1.15,
              }}>
                GDPR by design.<br/>
                <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.gold }}>Building toward NIS2 readiness · aligned to ISO/IEC 27001:2022.</em>
              </h3>
              <p style={{ fontSize: 16, lineHeight: 1.65, color: '#CBD5E1', marginTop: 20 }}>
                Every action is logged. Every export is recorded. Every auth event is traceable to user, IP and timestamp, immutably. Pull a full audit pack as a PDF in seconds. Keystone is built to support FSAI, HACCP and BRCGS traceability workflows for your team, certification of the platform itself is a roadmap item, not a claim.
              </p>

              <div style={{ marginTop: 28, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {['Supports GDPR', 'NIS2-readiness', 'ISO 27001 alignment', 'Supports BRCGS', 'Supports IFS Food', 'Supports EU food safety regulations'].map(b => (
                  <span key={b} style={{
                    fontSize: 11.5, fontWeight: 700, letterSpacing: '0.04em',
                    padding: '7px 12px',
                    background: 'rgba(201, 169, 97, 0.12)',
                    color: PALETTE.gold,
                    border: `1px solid ${PALETTE.gold}40`,
                    borderRadius: 100,
                  }}>{b}</span>
                ))}
              </div>
            </div>

            {/* Compliance Centre screenshot */}
            <div style={{
              borderRadius: 10,
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.12)',
              boxShadow: '0 28px 60px -15px rgba(0,0,0,0.5)',
              background: '#fff',
            }}>
              <div style={{
                background: '#F1ECE2', borderBottom: '1px solid #E5E1D8',
                padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#FF5F57' }} />
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#FEBC2E' }} />
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#28C840' }} />
              </div>
              <img
                src="/product/compliance-report.png"
                alt="Keystone, Traceability Events list with status workflow and PDF response pack export"
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 1100px) {
            .iso-head { grid-template-columns: 1fr !important; gap: 28px !important; }
            .iso-grid { grid-template-columns: 1fr 1fr !important; }
            .iso-grid .iso-cell:nth-child(odd) { border-right: 1px solid rgba(255,255,255,0.10) !important; }
            .iso-grid .iso-cell:nth-child(even) { border-right: none !important; }
            .iso-grid .iso-cell:nth-child(-n+2) { border-bottom: 1px solid rgba(255,255,255,0.10); }
            .comp-row { grid-template-columns: 1fr !important; gap: 32px !important; }
          }
          @media (max-width: 600px) {
            .iso-grid { grid-template-columns: 1fr !important; }
            .iso-grid .iso-cell { border-right: none !important; border-bottom: 1px solid rgba(255,255,255,0.10) !important; }
            .iso-grid .iso-cell:last-child { border-bottom: none !important; }
          }
        `}</style>
      </section>

      {/* FEATURE GRID */}
      <section style={{ background: PALETTE.paper, padding: '120px 32px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 80, alignItems: 'start', marginBottom: 80 }} className="feat-head">
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>What's in the box</div>
              <h2 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 700, color: PALETTE.navy, margin: 0, lineHeight: 1.05, letterSpacing: '-0.02em',
              }}>Twelve modules.<br/>One spine.</h2>
            </div>
            <p style={{ fontSize: 18, lineHeight: 1.55, color: PALETTE.slate, marginTop: 6 }}>
              Each module replaces a folder of spreadsheets, a sticky note on the freezer door, or that one person who knows where everything is. They share a single batch register, so trace queries collapse from hours to seconds.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, border: `1px solid ${PALETTE.border}` }} className="feat-grid">
            {[
              { n: '01', t: 'Sales Orders', d: 'Quick-entry, repeat-order memory, customer codes auto-mapped to your SKUs.' },
              { n: '02', t: 'Production Planner', d: 'Weekly grid. Drag jobs. See capacity. No more whiteboard photos in WhatsApp.' },
              { n: '03', t: 'Batch Traceability', d: 'Forward & backward in one query. Print FSAI-ready audit packs in seconds.' },
              { n: '04', t: 'Dispatch & Logistics', d: 'CMRs, pallet labels, temperature commitments, customer-specific layouts.' },
              { n: '05', t: 'QC & Compliance', d: 'Schedule samples, flag deviations, track corrective actions to closure.' },
              { n: '06', t: 'Reporting', d: 'Live numbers without exports. Margin, yield, dispatch volumes, QC pass-rate.' },
            ].map((f, i) => (
              <div key={f.n} style={{
                padding: 36,
                borderRight: (i + 1) % 3 === 0 ? 'none' : `1px solid ${PALETTE.border}`,
                borderBottom: i < 3 ? `1px solid ${PALETTE.border}` : 'none',
                background: PALETTE.paper,
                transition: 'background 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = PALETTE.bgSoft}
                onMouseLeave={e => e.currentTarget.style.background = PALETTE.paper}
              >
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 14, color: PALETTE.magenta, fontWeight: 700, letterSpacing: '0.1em' }}>{f.n}</div>
                <h3 style={{ fontSize: 22, fontWeight: 700, color: PALETTE.navy, margin: '14px 0 12px', letterSpacing: '-0.01em' }}>{f.t}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.6, color: PALETTE.slate, margin: 0 }}>{f.d}</p>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @media (max-width: 900px) { .feat-head { grid-template-columns: 1fr !important; gap: 32px !important; } }
          @media (max-width: 800px) { .feat-grid { grid-template-columns: 1fr 1fr !important; } .feat-grid > div { border-right: none !important; border-bottom: 1px solid ${PALETTE.border} !important; } }
          @media (max-width: 480px) { .feat-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </section>

      {/* TRIGGER EVENTS, for passive buyers */}
      <section style={{ background: PALETTE.cream, padding: '96px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 80, alignItems: 'end', marginBottom: 44 }} className="trig-head">
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>
                The day it stops being optional
              </div>
              <h2 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(32px, 4.4vw, 50px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05,
              }}>
                “We’re fine for now.”<br/>
                <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>Until you’re not.</em>
              </h2>
            </div>
            <p style={{ fontSize: 17, lineHeight: 1.55, color: PALETTE.slate, margin: 0, maxWidth: 520 }}>
              Six moments where the existing setup stops working overnight. If any is on your radar inside twelve months, you want Keystone in place <strong style={{ color: PALETTE.navy, fontWeight: 700 }}>before</strong> the day, not during it.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 0,
            border: `1px solid ${PALETTE.border}`,
            background: PALETTE.paper,
          }} className="trig-grid">
            {[
              { eyebrow: 'Compliance', t: 'The day Tesco asks for a 60-minute trace.', d: 'A retailer wants full forward + backward trace as a PDF, in your branding, in the next hour.' },
              { eyebrow: 'Audit', t: 'The day SFPA arrives unannounced.', d: 'A fortnight of audit prep is now expected in 48 hours, with a chain-of-custody log.' },
              { eyebrow: 'Key staff', t: 'The day the dispatch person resigns.', d: 'Sixty percent of the operation lived in their head. It has 14 days to live in a system instead.' },
              { eyebrow: 'Growth', t: 'The day a second retailer says yes.', d: 'EDI, ASN files, retailer labels, code mapping. Excel already creaks at one retailer.' },
              { eyebrow: 'Succession', t: 'The day you think about exit.', d: '“Show me your traceability data” is the diligence question that crashes deals built on Excel.' },
              { eyebrow: 'Recall', t: 'The day a recall is real.', d: 'Six hours to identify every batch, every dispatch, every affected customer. Spreadsheets miss one.' },
            ].map((s, i) => (
              <div key={s.t} style={{
                padding: 28,
                borderRight: (i + 1) % 3 === 0 ? 'none' : `1px solid ${PALETTE.border}`,
                borderBottom: i < 3 ? `1px solid ${PALETTE.border}` : 'none',
              }} className="trig-cell">
                <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.muted, marginBottom: 12 }}>
                  {s.eyebrow}
                </div>
                <h3 style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 20, fontWeight: 700, color: PALETTE.navy,
                  margin: 0, letterSpacing: '-0.01em', lineHeight: 1.2,
                }}>{s.t}</h3>
                <p style={{ fontSize: 13.5, lineHeight: 1.55, color: PALETTE.slate, marginTop: 10, marginBottom: 0 }} dangerouslySetInnerHTML={{ __html: s.d }} />
              </div>
            ))}
          </div>

          <div style={{
            marginTop: 24,
            padding: '18px 26px',
            background: PALETTE.navy,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 20,
            flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 14.5, color: '#E5E7EB' }}>
              <strong style={{ color: PALETTE.gold, fontWeight: 700 }}>Move when it’s calm.</strong> Not at midnight with the auditor in the car park.
            </div>
            <Link to="/site/contact" style={{
              padding: '11px 20px',
              background: PALETTE.gold,
              color: PALETTE.navy,
              textDecoration: 'none',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              borderRadius: 4,
              flexShrink: 0,
            }}>
              Get ahead of it →
            </Link>
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) {
            .trig-head { grid-template-columns: 1fr !important; gap: 32px !important; }
            .trig-grid { grid-template-columns: 1fr 1fr !important; }
            .trig-cell { border-right: none !important; border-bottom: 1px solid ${PALETTE.border} !important; }
            .trig-grid > .trig-cell:nth-child(odd) { border-right: 1px solid ${PALETTE.border} !important; }
          }
          @media (max-width: 540px) {
            .trig-grid { grid-template-columns: 1fr !important; }
            .trig-grid > .trig-cell { border-right: none !important; }
          }
        `}</style>
      </section>

      {/* WHITE-GLOVE ONBOARDING, for the "I don't have time" objection */}
      <section style={{ background: PALETTE.paper, padding: '120px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.2fr',
            gap: 64,
            alignItems: 'center',
          }} className="wg-row">
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>
                "We don't have time to implement."
              </div>
              <h2 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(30px, 4vw, 46px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05,
              }}>
                You don't.<br/>
                <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>That's our job.</em>
              </h2>
              <p style={{ fontSize: 17, lineHeight: 1.65, color: PALETTE.slate, marginTop: 24 }}>
                Every food manufacturer we've ever talked to says the same thing in week one, "we know we need this, we just can't stop production for two months to do it." Neither could Cashel Blue. So we built around that.
              </p>
              <p style={{ fontSize: 16, lineHeight: 1.6, color: PALETTE.slate, marginTop: 16 }}>
                Your team gives us <strong style={{ color: PALETTE.navy, fontWeight: 600 }}>90 minutes a week</strong> across the implementation. We do everything else: data extraction, customer code mapping, dispatch templates, the whole rebuild. You keep dispatching. We come to you.
              </p>
            </div>
            <div style={{
              padding: 36,
              background: PALETTE.cream,
              border: `1px solid ${PALETTE.border}`,
              borderTop: `4px solid ${PALETTE.gold}`,
              borderRadius: 8,
            }}>
              <div style={{
                fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
                fontWeight: 700, color: PALETTE.magenta, marginBottom: 18,
              }}>What we handle, so you don't</div>
              <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                {[
                  ['Data extraction', 'We pull your spreadsheets, customer lists, products, codes, without you hand-typing a single row.'],
                  ['On-site mapping', 'Two days on your floor. We sit with the dispatch desk, the QC bench, the production planner. We watch how it actually flows.'],
                  ['Configuration', 'Templates, code mapping, retailer EDI, conversion formulas, all done by us, for you.'],
                  ['Cut-over support', 'We sit beside you for the first three production days post go-live. Mistakes are ours to fix, not yours.'],
                  ['30-day hand-holding', 'WhatsApp / Teams / call. Direct line to the people who built it. Not a ticket queue.'],
                ].map(([t, d]) => (
                  <li key={t} style={{
                    display: 'flex', gap: 14,
                    padding: '12px 0',
                    borderBottom: `1px solid ${PALETTE.border}`,
                  }}>
                    <span style={{
                      flexShrink: 0,
                      marginTop: 4,
                      width: 18, height: 18,
                      borderRadius: '50%',
                      background: PALETTE.navy,
                      color: PALETTE.gold,
                      fontSize: 11, fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>✓</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: PALETTE.navy }}>{t}</div>
                      <div style={{ fontSize: 13, color: PALETTE.slate, marginTop: 2, lineHeight: 1.5 }}>{d}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) { .wg-row { grid-template-columns: 1fr !important; gap: 32px !important; } }
        `}</style>
      </section>

      {/* IMAGE BREAK + QUOTE */}
      <section style={{
        position: 'relative',
        height: 520,
        backgroundImage: `linear-gradient(rgba(10,37,64,0.78), rgba(10,37,64,0.6)), url(${IMG_LINE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 32px',
      }}>
        <blockquote style={{
          maxWidth: 920,
          margin: 0,
          textAlign: 'center',
          color: '#fff',
        }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 24 }}>
            What we built it for
          </div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(26px, 3.6vw, 42px)', lineHeight: 1.25, fontWeight: 500, fontStyle: 'italic', letterSpacing: '-0.01em' }}>
            To replace four spreadsheets, two whiteboards, and a chronic Sunday-night anxiety <span style={{ fontStyle: 'normal', color: PALETTE.gold, fontWeight: 600 }}>with one screen.</span>
          </div>
        </blockquote>
      </section>

      {/* QUIET CTA */}
      <section style={{ background: PALETTE.cream, padding: '120px 32px' }}>
        <div style={{ maxWidth: 880, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 18 }}>The next step</div>
          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(32px, 4.4vw, 52px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05,
          }}>
            Twenty minutes.<br/><em style={{ fontStyle: 'italic', color: PALETTE.magenta, fontWeight: 500 }}>No deck.</em>
          </h2>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: PALETTE.slate, marginTop: 24, maxWidth: 620, marginLeft: 'auto', marginRight: 'auto' }}>
            We won't show slides. You tell us what's broken. If we're not the right fit, we'll say so.
          </p>
          <Link
            to="/site/contact"
            data-testid="footer-cta"
            style={{
              display: 'inline-block',
              marginTop: 40,
              padding: '18px 36px',
              background: PALETTE.navy,
              color: '#fff',
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              borderRadius: 4,
            }}
          >
            Book the call →
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
